import React from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, Handshake, ShieldCheck, 
  Building2, Quote, Bike, 
  Target, Award 
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

const About = () => {
  const values = [
    {
      icon: <Clock />,
      title: "Punctual Fleet Support",
      description: "In Budapest's fast-paced market, timing is everything. Our 55+ bikers ensure your service level agreements are always met."
    },
    {
      icon: <Handshake />,
      title: "Reliable Partnership",
      description: "We act as a seamless extension of your delivery operations, providing elite workforce for restaurants and e-commerce."
    },
    {
      icon: <ShieldCheck />,
      title: "Physical Excellence",
      description: "20,000+ successful deliveries prove our mastery of Budapest's streets and professional handling standards."
    }
  ];

  const partners = ["Express fork kft", "Wolt", "GM capital 2020", "Ut Globalnextgensolution kft"];

  return (
    <main className="min-h-screen bg-white text-[#032B53] selection:bg-[#0099FF] selection:text-white">
      
      {/* 1. HERO SECTION - Minimal & Professional */}
      <section className="relative pt-32 pb-24 bg-[#032B53] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1551867633-194f125bddfa?q=80&w=2000&auto=format&fit=crop" 
            alt="Budapest Fleet Operations" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#032B53]/80 to-[#032B53]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-8">
              <span className="h-[1px] w-10 bg-[#0099FF]"></span>
              <span className="text-[#0099FF] font-bold tracking-[0.3em] text-[10px] uppercase">Established 2023</span>
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white leading-[0.9] tracking-tighter mb-10">
              Budapest's Elite <br />
              <span className="text-white/30 italic">Workforce.</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/70 font-light leading-relaxed max-w-2xl">
              UCorier KFT provides high-capacity bike courier fleets for the city's leading 3PL platforms and corporate partners.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. CEO SECTION - High Contrast */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-20 items-center">
            <motion.div 
              {...fadeInUp}
              className="w-full md:w-1/2 relative"
            >
              <div className="relative z-10 rounded-[3rem] overflow-hidden shadow-2xl">
                <img 
                  src="/ceo umer hayat.jpeg" 
                  alt="Umer Hayat CEO UCorier" 
                  className="w-full grayscale hover:grayscale-0 transition-all duration-1000"
                />
              </div>
              <div className="absolute -bottom-10 -right-10 bg-[#0099FF] p-10 rounded-full hidden lg:block">
                <Quote className="text-white h-10 w-10" />
              </div>
            </motion.div>
            
            <motion.div 
              {...fadeInUp}
              transition={{ delay: 0.2 }}
              className="w-full md:w-1/2 space-y-8"
            >
              <div className="space-y-2">
                <h2 className="text-4xl md:text-6xl font-black tracking-tighter">Physical Excellence.</h2>
                <p className="text-[#0099FF] font-bold uppercase tracking-widest text-xs">A Message from the Founder</p>
              </div>
              
              <div className="space-y-6 text-gray-500 text-lg leading-relaxed font-medium">
                <p className="text-[#032B53] text-xl font-bold">
                  "At UCorier KFT, we don't build apps. We build the most reliable physical delivery workforce in Hungary."
                </p>
                <p>
                  As CEO, my focus is on **Physical Fleet Scalability**. By managing 55+ professional bikers, we provide the ground support that partners like **Wolt** need to maintain market dominance.
                </p>
                <p>
                  Our 20,000+ delivery milestone reflects our discipline, local Budapest expertise, and commitment to business-to-business logistics.
                </p>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <p className="text-3xl font-black tracking-tighter text-[#032B53]">Umer Hayat</p>
                <p className="text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px]">Founder & Chief Executive</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. OPERATIONAL HUB - Building architecture focus */}
      <section className="bg-gray-50 py-32 rounded-[4rem] mx-4">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp} className="space-y-10">
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-tight">
                Strategic Hub <br/> Budapest XIV
              </h2>
              <p className="text-gray-500 text-xl font-medium leading-relaxed">
                Operating out of **Thököly út 45**, our central dispatch center is positioned to reach any district in the city within minutes.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
                  <span className="block text-4xl font-black text-[#032B53]">55+</span>
                  <span className="text-[10px] text-[#0099FF] font-bold uppercase tracking-widest">Active Fleet</span>
                </div>
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
                  <span className="block text-4xl font-black text-[#032B53]">20k+</span>
                  <span className="text-[10px] text-[#0099FF] font-bold uppercase tracking-widest">Orders Handled</span>
                </div>
              </div>

              <div className="flex items-center gap-4 text-[#032B53]">
                <div className="w-12 h-12 bg-[#0099FF]/10 rounded-2xl flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-[#0099FF]" />
                </div>
                <span className="font-bold tracking-tight">1146 Budapest, Thököly út 45.</span>
              </div>
            </motion.div>

            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="rounded-[3.5rem] overflow-hidden shadow-2xl h-[600px]"
            >
              <img 
                src="https://images.unsplash.com/photo-1616432902940-b7a1acbc60b3?q=80&w=870&auto=format&fit=crop" 
                alt="Budapest Architecture" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PARTNERSHIPS */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-400 font-bold uppercase tracking-[0.4em] text-[10px] mb-16">The Market Leaders Trust Us</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {partners.map((partner, i) => (
              <motion.div 
                key={i} 
                whileHover={{ y: -5 }}
                className="flex items-center justify-center p-10 border border-gray-100 rounded-[2rem] hover:border-[#0099FF] transition-colors"
              >
                <span className="text-lg font-black text-[#032B53] uppercase tracking-tighter">{partner}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. CORE VALUES - Dark Minimalist */}
      <section className="py-32 bg-[#032B53] rounded-[4rem] mx-4 mb-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-4xl md:text-6xl font-black text-white tracking-tighter">The Fleet Standards</h2>
            <p className="text-[#0099FF] font-bold uppercase tracking-widest text-xs">Our Commitment to Quality</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-10">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="group p-12 bg-[#04386b] rounded-[3rem] hover:bg-[#0099FF] transition-all duration-500"
              >
                <div className="bg-[#032B53] w-14 h-14 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-white transition-colors">
                  {React.cloneElement(value.icon, { size: 24, className: "text-[#0099FF]" })}
                </div>
                <h3 className="text-2xl font-black text-white mb-6 tracking-tight">{value.title}</h3>
                <p className="text-blue-100/50 group-hover:text-white transition-colors leading-relaxed font-medium">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Minimal Footer Tagline */}
      <section className="py-20 text-center">
        <Bike size={20} className="mx-auto text-[#0099FF] mb-6 opacity-30" />
        <p className="text-[10px] font-bold text-gray-300 uppercase tracking-[0.5em]">Budapest Delivery Infrastructure</p>
      </section>
    </main>
  );
};

export default About;